import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import EmptyNotificationsIcon from '../../../assets/emptyViews/EmptyNotificationsIcon';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import {
  Container,
  NotificationsList,
  StyledDiv,
} from '../../notifications/NotificationsPage/NotificationsPage.styles';
import NotificationsItem from './NotificationsItem';
import {
  EmptyNotificationsMessage,
  HeaderContainer,
  NotificationDivider,
  NotificationItemContainer,
  NotificationsEmptyContainer,
  ShimmerItemContainer,
  ShimmerTextContainer,
  StyledDefaultHeader,
  StyledImageShimmer,
  StyledImageShimmerContainer,
  StyledInfiniteScroll,
  StyledTextShimmer,
} from './NotificationsSection.styles';

export enum NotificationFilter {
  All = 'All',
  Unread = 'Unread',
  Read = 'Read',
}

export enum ScrollReference {
  Window = 'window',
  NotificationList = 'notificationList',
}

interface NotificationsSectionProps {
  header?: ReactNode;
  scrollReference?: ScrollReference;
  /**
   * If true, the component rendered is accessed through URL in mobile devices.
   */
  isMobile?: boolean;
}

const ShimmerNotificationItem = () => {
  return (
    <NotificationItemContainer>
      <ShimmerItemContainer>
        <StyledImageShimmerContainer>
          <StyledImageShimmer />
        </StyledImageShimmerContainer>
        <ShimmerTextContainer>
          <StyledTextShimmer />
          <StyledTextShimmer $width={'75px'} />
        </ShimmerTextContainer>
      </ShimmerItemContainer>
      <NotificationDivider>
        <hr />
      </NotificationDivider>
    </NotificationItemContainer>
  );
};

const DefaultHeader = () => {
  const { t } = useTranslation();
  return <StyledDefaultHeader>{t('notifications.title')}</StyledDefaultHeader>;
};

const NotificationsSection = ({
  header = <DefaultHeader />,
  scrollReference = ScrollReference.Window,
  isMobile = false,
}: NotificationsSectionProps) => {
  const { notifications, loadMoreNotifications, markAsRead, loadingNotifications, resetUnreadCount } =
    useNotificationContext();
  const notificationsData = notifications();
  const notificationsNodes = notificationsData?.notifications.nodes;
  const { t } = useTranslation();

  // Reset unread count when notifications are loaded.
  useEffect(() => {
    if (isMobile && notificationsNodes?.length > 0) {
      resetUnreadCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationsNodes, isMobile]);

  return (
    <>
      {!loadingNotifications && notificationsNodes?.length > 0 && (
        <Container>
          <HeaderContainer>{header}</HeaderContainer>
          <NotificationsList id={ScrollReference.NotificationList}>
            <StyledInfiniteScroll
              initialLoad={false}
              loadMore={loadMoreNotifications}
              hasMore={notificationsData?.notifications.pageInfo.hasNextPage}
              loader={null}
              useWindow={scrollReference === ScrollReference.Window}
              threshold={250}
            >
              {notificationsNodes?.map((notification, index) => (
                <StyledDiv key={index}>
                  <NotificationItemContainer hoverEffect>
                    <NotificationsItem
                      id={notification.id}
                      title={notification.messageHtml}
                      date={notification.createdAt}
                      markAsRead={() => markAsRead(notification.id)}
                      action={notification.action}
                      entityId={notification.entityId}
                      vehicleImgUrl={notification.imageUrl}
                      isRead={notification.clicked}
                    />
                    <NotificationDivider>
                      <hr />
                    </NotificationDivider>
                  </NotificationItemContainer>
                </StyledDiv>
              ))}
            </StyledInfiniteScroll>
          </NotificationsList>
        </Container>
      )}
      {!loadingNotifications && notificationsNodes?.length === 0 && (
        <Container>
          <HeaderContainer>{header}</HeaderContainer>
          <NotificationsEmptyContainer>
            <EmptyNotificationsIcon />
            <EmptyNotificationsMessage>{t('notifications.noNotifications')}</EmptyNotificationsMessage>
          </NotificationsEmptyContainer>
        </Container>
      )}
      {loadingNotifications && (
        <Container>
          <HeaderContainer>{header}</HeaderContainer>
          {[...Array(10)].map((_, i) => (
            <ShimmerNotificationItem key={`ShimmerNotificationItem-${i}`} />
          ))}
        </Container>
      )}
    </>
  );
};

export default NotificationsSection;
